// Past-day artifact. No composer (per PRD §5.7). Same Header (renders prev/next).
// Replaces UpNext + NextFew with: day summary stats, events list, that-night card.
// Rows are tappable (edit/delete) and an "Add an entry" button backfills a
// forgotten log onto this day.

import type { EventRow } from "../lib/events";
import { gramsToLbOz, nursingOzEstimate } from "../lib/events";
import { daytimeSleepMinutesForDay } from "../lib/schedule";
import { DayRibbon } from "./DayRibbon";
import { EventIcon } from "./icons";
import type { DayPlan, PlanEvent } from "../lib/dayPlan";
import { hm, durStr } from "../lib/timeFmt";
import { useT, useAmount } from "../lib/LocaleContext";
import type { Strings } from "../lib/i18n";

type AmountFmt = ReturnType<typeof useAmount>;

type Props = {
  plan: DayPlan;
  events: EventRow[]; // full event list for the day (already filtered upstream)
  viewDate: Date;
  /** Opens the backfill sheet to add a forgotten entry on this day. */
  onAddEntry?: () => void;
  /** Tapping a logged row opens the edit sheet, same as on the Today timeline. */
  onEditEvent?: (event: EventRow) => void;
};

export function PastDayView({ plan, events, viewDate, onAddEntry, onEditEvent }: Props) {
  const t = useT();
  const amount = useAmount();
  const summary = summarizeDay(events, viewDate, t);
  const night = summarizeNight(events, viewDate);

  return (
    <>
      <DayRibbon plan={plan} />
      <div style={styles.statsRow}>
        <Stat label={t.pastDay.intake} big={summary.totalOz != null ? t.pastDay.ozValue(amount.fmt(summary.totalOz)) : t.pastDay.dash} />
        <Stat label={t.pastDay.daytimeSleep} big={summary.daytimeSleepMin > 0 ? durStr(summary.daytimeSleepMin) : t.pastDay.dash} />
        <Stat label={t.pastDay.longestStretch} big={night.longestStretchMin > 0 ? durStr(night.longestStretchMin) : t.pastDay.dash} />
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>{t.pastDay.events}</div>
        {plan.events.length === 0 ? (
          <div style={styles.empty}>{t.pastDay.empty}</div>
        ) : (
          <div style={styles.list}>
            {plan.events.map((e, i) => {
              const source = onEditEvent ? events.find((ev) => ev.id === e.id) : undefined;
              const inner = (
                <>
                  <span className="bh-num" style={styles.rowTime}>
                    {hm(e.start)}
                  </span>
                  <div style={{ ...styles.rowIcon, ...iconColor(e) }}>
                    <EventIcon kind={e.kind} size={14} />
                  </div>
                  <span style={styles.rowLabel}>{labelFor(e, t)}</span>
                  <span style={{ flex: 1 }} />
                  <span className="bh-num" style={styles.rowDetail}>
                    {detailFor(e, t, amount)}
                  </span>
                </>
              );
              const rowStyle = { ...styles.row, borderBottom: i === plan.events.length - 1 ? "0" : "1px solid var(--bh-divider)" };
              return source ? (
                <button key={e.id} style={{ ...styles.rowButton, ...rowStyle }} onClick={() => onEditEvent!(source)}>
                  {inner}
                </button>
              ) : (
                <div key={e.id} style={rowStyle}>
                  {inner}
                </div>
              );
            })}
          </div>
        )}
        {onAddEntry && (
          <button style={styles.addEntry} onClick={onAddEntry}>
            + {t.addEntry.addButton}
          </button>
        )}
      </div>

      {(night.longestStretchMin > 0 || night.nightFeeds > 0) && (
        <div style={styles.nightCard}>
          <div style={styles.nightLabel}>{t.pastDay.thatNight}</div>
          <div style={styles.nightBody}>
            <span className="bh-num" style={styles.nightBig}>
              {durStr(night.longestStretchMin)}
            </span>
            {t.pastDay.nightSummary(night.nightFeeds)}
            {night.feedTimes && t.pastDay.feedsAt(night.feedTimes)}
          </div>
        </div>
      )}

      {summary.weightChange && (
        <div style={styles.nightCard}>
          <div style={styles.nightLabel}>{t.pastDay.weight}</div>
          <div style={styles.nightBody}>{summary.weightChange}</div>
        </div>
      )}
    </>
  );
}

function Stat({ label, big }: { label: string; big: string }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div className="bh-num" style={styles.statBig}>
        {big}
      </div>
    </div>
  );
}

function labelFor(e: PlanEvent, t: Strings): string {
  if (e.kind === "feed") return t.pastDay.labelBottle;
  if (e.kind === "nurse") return t.pastDay.labelNursed;
  if (e.kind === "sleep") return (e.durationMin ?? 0) >= 4 * 60 ? t.pastDay.labelNightSleep : t.pastDay.labelNap;
  if (e.kind === "wake") return t.pastDay.labelWake;
  if (e.kind === "bedtime") return t.pastDay.labelBedtime;
  return e.kind;
}

function detailFor(e: PlanEvent, t: Strings, amount: AmountFmt): string {
  if (e.kind === "feed" && e.amountOz != null) return t.pastDay.detailOz(amount.fmt(e.amountOz));
  if (e.kind === "nurse" && e.durationMin != null) return t.pastDay.detailMin(e.durationMin);
  if (e.kind === "sleep" && e.durationMin) return durStr(e.durationMin);
  return "";
}

function iconColor(e: PlanEvent): React.CSSProperties {
  if (e.kind === "feed" || e.kind === "nurse") return { background: "var(--bh-feed-soft)", color: "var(--bh-feed)" };
  if (e.kind === "sleep" || e.kind === "bedtime") return { background: "var(--bh-sleep-soft)", color: "var(--bh-sleep)" };
  return { background: "var(--bh-surface-2)", color: "var(--bh-ink-3)" };
}

function summarizeDay(events: EventRow[], date: Date, t: Strings): { totalOz: number | null; daytimeSleepMin: number; weightChange: string | null } {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  let totalOz = 0;
  let hasFeedOz = false;
  // Same 7am–7pm clipped-window rule as the Today screen's guidance strip.
  const daytimeSleepMin = daytimeSleepMinutesForDay(events, date);
  let weightChange: string | null = null;

  for (const e of events) {
    const ts = new Date(e.at).getTime();
    if (ts < dayStart.getTime() || ts >= dayEnd.getTime()) continue;
    if (e.type === "feed" && e.amount_oz != null) {
      totalOz += e.amount_oz;
      hasFeedOz = true;
    } else if (e.type === "nurse" && e.amount_oz != null) {
      totalOz += e.amount_oz;
      hasFeedOz = true;
    } else if (e.type === "nurse" && e.duration_min != null) {
      totalOz += nursingOzEstimate(e.duration_min);
      hasFeedOz = true;
    }
    if (e.type === "weight" && e.weight_grams != null) {
      const { lb, oz } = gramsToLbOz(e.weight_grams);
      weightChange = t.pastDay.weighedToday(lb, oz);
    }
  }

  return { totalOz: hasFeedOz ? totalOz : null, daytimeSleepMin, weightChange };
}

function summarizeNight(events: EventRow[], date: Date): { longestStretchMin: number; nightFeeds: number; feedTimes: string } {
  // Night window: 7pm of `date` → 7am of next day.
  const nightStart = new Date(date);
  nightStart.setHours(19, 0, 0, 0);
  const nightEnd = new Date(nightStart);
  nightEnd.setDate(nightEnd.getDate() + 1);
  nightEnd.setHours(7, 0, 0, 0);

  // Find feeds inside the night window. Sort by time.
  const feeds = events
    .filter((e) => (e.type === "feed" || e.type === "nurse"))
    .map((e) => new Date(e.at).getTime())
    .filter((t) => t >= nightStart.getTime() && t < nightEnd.getTime())
    .sort((a, b) => a - b);

  // Longest stretch between consecutive feeds (or nightStart→first feed, last feed→nightEnd).
  let longest = 0;
  const boundaries = [nightStart.getTime(), ...feeds, nightEnd.getTime()];
  for (let i = 1; i < boundaries.length; i++) {
    const gap = boundaries[i] - boundaries[i - 1];
    if (gap > longest) longest = gap;
  }

  const feedTimes = feeds
    .slice(0, 3)
    .map((t) => new Date(t).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }))
    .join(", ");
  return {
    longestStretchMin: Math.round(longest / 60_000),
    nightFeeds: feeds.length,
    feedTimes: feeds.length ? feedTimes : "",
  };
}

const styles: Record<string, React.CSSProperties> = {
  statsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
    margin: "0 14px 16px",
  },
  stat: {
    background: "var(--bh-surface)",
    borderRadius: 16,
    padding: 14,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 6,
  },
  statBig: { fontSize: 22, fontWeight: 600, color: "var(--bh-ink)", lineHeight: 1 },
  section: { margin: "0 14px 16px" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 8,
    padding: "0 4px",
  },
  empty: {
    background: "var(--bh-surface)",
    borderRadius: 16,
    padding: 16,
    fontSize: 14,
    color: "var(--bh-ink-3)",
  },
  list: {
    background: "var(--bh-surface)",
    borderRadius: 20,
    padding: "4px 16px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 0",
    minHeight: 48,
  },
  rowButton: {
    appearance: "none",
    width: "100%",
    background: "transparent",
    border: "none",
    borderRadius: 0,
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  addEntry: {
    appearance: "none",
    width: "100%",
    marginTop: 10,
    background: "var(--bh-surface)",
    color: "var(--bh-ink-2)",
    border: "1px dashed var(--bh-divider)",
    padding: "14px",
    borderRadius: 16,
    fontSize: 15,
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    minHeight: 52,
  },
  rowTime: { width: 60, fontSize: 14, color: "var(--bh-ink-3)" },
  rowIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rowLabel: { fontSize: 16, color: "var(--bh-ink)" },
  rowDetail: { fontSize: 14, color: "var(--bh-ink-3)" },
  nightCard: {
    margin: "0 14px 14px",
    padding: 16,
    background: "var(--bh-surface)",
    borderRadius: 18,
  },
  nightLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 6,
  },
  nightBody: { fontSize: 15, color: "var(--bh-ink)", lineHeight: 1.5 },
  nightBig: { fontSize: 20, fontWeight: 700 },
};
